import React, { useState, useRef, useEffect } from 'react';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getEvents, getImageUrl, formatEventDate } from '../../utils/api';
import { events as mockEvents } from '../../data/mockData';
import './UpcomingEvents.css';

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      if (data && data.length > 0) {
        // Format events from API
        const formattedEvents = data.map(event => ({
          ...event,
          displayDate: formatDisplayDate(event.date),
          ticketLink: '#'
        }));
        setEvents(formattedEvents);
      } else {
        setEvents(mockEvents);
      }
    } catch (error) {
      console.log('Using mock data for events');
      setEvents(mockEvents);
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    const result = formatEventDate(dateStr);
    return `${result.month} ${result.day}`;
  };

  const handleMouseMove = (e, index) => {
    if (window.innerWidth < 1024) return; // Disable 3D tilt on mobile/tablet

    const card = cardRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  };

  const handleMouseLeave = (index) => {
    setHoveredCard(null);
    const card = cardRefs.current[index];
    if (card) {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale(1)';
    }
  };

  if (loading) {
    return (
      <section id="events" className="upcoming-events">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Events</span>
            <h2>Where the records meet the room</h2>
            <p>AMR extends beyond the release page. From late-night sessions to future live experiences, this is where the label moves into physical space.</p>
          </div>
          <div className="loading-spinner">Loading events...</div>
        </div>
      </section>
    );
  }

  if (events.length === 0) {
    return (
      <section id="events" className="upcoming-events">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Events</span>
            <h2>Where the records meet the room</h2>
            <p>AMR extends beyond the release page. From late-night sessions to future live experiences, this is where the label moves into physical space.</p>
          </div>
          <p className="no-events-message">No event announced yet. Stay close — we're building the world in real time.</p>
          <div className="events-cta">
            <Link to="/events" className="btn btn-primary">View Events</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="events" className="upcoming-events">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Events</span>
          <h2>Where the records meet the room</h2>
          <p>AMR extends beyond the release page. From late-night sessions to future live experiences, this is where the label moves into physical space.</p>
        </div>

        <div className="events-grid">
          {events.map((event, index) => (
            <div
              key={event.id}
              ref={(el) => (cardRefs.current[index] = el)}
              className="event-card fade-in"
              data-testid={`event-card-${event.id}`}
              onMouseMove={(e) => {
                setHoveredCard(index);
                handleMouseMove(e, index);
              }}
              onMouseLeave={() => handleMouseLeave(index)}
            >
              <div className="event-image-wrapper">
                <img 
                  src={getImageUrl(event.image)} 
                  alt={event.title}
                  className="event-image grayscale-image"
                  loading="lazy"
                />
                <div className="event-date-badge">
                  <span className="date-text">{event.displayDate}</span>
                </div>
                <div className="event-overlay">
                  <div className="lineup">
                    <h4>Lineup:</h4>
                    {(event.lineup || []).map((artist, idx) => (
                      <span key={idx}>{artist}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="event-info">
                <h3>{event.title}</h3>
                
                <div className="event-details">
                  <div className="event-detail">
                    <MapPin size={16} />
                    <span>{event.venue}, {event.location || event.city}</span>
                  </div>
                  <div className="event-detail">
                    <Clock size={16} />
                    <span>{event.time}</span>
                  </div>
                </div>

                <Link 
                  to={`/events/${event.slug || event.id}`} 
                  className="btn btn-secondary event-btn"
                  data-testid={`event-details-btn-${event.id}`}
                >
                  View Details & Register
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
